// Menu contextuel ancré à un bouton (popover) ; sur téléphone, avec { feuille: true }, il monte du bas de l'écran.
// Items : { label, icone, action(fn) | href (externe: nouvel onglet), danger, courant, titre } ; { sep: true } trace un séparateur.
// Clavier : flèches, Home/End, Échap ; clic hors du menu ; aria-expanded posé sur l'ancre.
// ouvrirFormulaire(ancre, opts) : le même popover, avec un champ et deux boutons, rendu en promesse (valeur ou null) :
// choisir une date, nommer quelque chose, sans quitter la page ni ouvrir une fenêtre.
import { html } from '../../core/html.js';
import { ico } from '../icones.js';
var ouvert = null;

export function fermerMenu() {
    if (!ouvert) return;
    var o = ouvert; ouvert = null;
    o.menu.classList.add('sortie'); o.menu.classList.remove('ouvert');
    o.ancre.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', o.clavier, true);
    setTimeout(function() { o.menu.remove(); o.voile.remove(); }, 140);
    if (o.rendreFocus && o.ancre.focus) { try { o.ancre.focus(); } catch (e) {} }
    if (o.auFermer) o.auFermer();
}

// Le popover lui-même : voile, boîte, position sous l'ancre (jamais hors écran) ou feuille basse sur téléphone.
function poser(ancre, contenu, opts) {
    if (ouvert && ouvert.ancre === ancre) { fermerMenu(); return null; }
    fermerMenu();
    var mobile = window.innerWidth < 768, feuille = mobile && opts.feuille;
    var doigt = false; try { doigt = window.matchMedia('(pointer: coarse)').matches; } catch (e) {}
    var voile = document.createElement('div'); voile.className = 'voile-menu' + (feuille ? ' feuille' : '');
    var menu = document.createElement('div'); menu.className = 'menu' + (feuille ? ' feuille' : '') + (opts.classe ? ' ' + opts.classe : '');
    menu.setAttribute('role', opts.role || 'menu');
    if (opts.label) menu.setAttribute('aria-label', opts.label);
    menu.innerHTML = String(html`${feuille ? html`<div class="menu-poignee"></div>` : ''}${contenu}`);
    document.body.appendChild(voile); document.body.appendChild(menu);
    if (!feuille) {
        var r = ancre.getBoundingClientRect(), largeur = Math.min(320, Math.max(220, menu.offsetWidth || 220));
        var gauche = opts.alignement === 'gauche' ? r.left : r.right - largeur;
        gauche = Math.max(8, Math.min(gauche, window.innerWidth - largeur - 8));
        var haut = r.bottom + 6;
        menu.style.left = gauche + 'px'; menu.style.top = haut + 'px'; menu.style.minWidth = largeur + 'px';
        menu.style.setProperty('--origine', opts.alignement === 'gauche' ? 'top left' : 'top right');
        var hauteur = menu.offsetHeight;
        if (haut + hauteur > window.innerHeight - 8) { menu.style.top = Math.max(8, r.top - hauteur - 6) + 'px'; menu.style.setProperty('--origine', 'bottom right'); }
    }
    ancre.setAttribute('aria-expanded', 'true');
    voile.addEventListener('click', fermerMenu);
    ouvert = { ancre: ancre, menu: menu, voile: voile, clavier: null, rendreFocus: true, auFermer: null };
    requestAnimationFrame(function() { requestAnimationFrame(function() { menu.classList.add('ouvert'); if (opts.auMontrer) opts.auMontrer(doigt); }); });
    return ouvert;
}

export function ouvrirMenu(ancre, items, opts) {
    opts = opts || {};
    var contenu = html`${opts.titre ? html`<div class="menu-titre">${opts.titre}</div>` : ''}${items.map(function(it) {
        if (it.sep) return html`<div class="menu-sep" role="separator"></div>`;
        var classes = 'menu-item' + (it.danger ? ' danger' : '');
        var corps = html`${it.icone ? ico(it.icone, { taille: 18 }) : ''}<span>${it.label}</span>`;
        var titre = it.titre ? html` title="${it.titre}"` : '';
        if (it.href) return html`<a class="${classes}" role="menuitem" href="${it.href}"${it.courant ? ' aria-current="page"' : ''}${it.externe ? html` target="_blank" rel="noopener"` : ''}${titre}>${corps}</a>`;
        return html`<button type="button" class="${classes}" role="menuitem"${titre}>${corps}</button>`;
    })}`;
    var boutons = [];
    var o = poser(ancre, contenu, { feuille: opts.feuille, label: opts.label, alignement: opts.alignement, auMontrer: function(doigt) { var p = boutons[0]; if (p && !doigt) p.focus(); } });
    if (!o) return;
    boutons = Array.prototype.slice.call(o.menu.querySelectorAll('[role=menuitem]'));
    boutons.forEach(function(b, i) {
        var it = items.filter(function(x) { return !x.sep; })[i];
        b.addEventListener('click', function() { if (it.action) { fermerMenu(); it.action(); } else setTimeout(fermerMenu, 0); });
    });
    o.clavier = function(ev) {
        var i = boutons.indexOf(document.activeElement);
        if (ev.key === 'Escape') { ev.preventDefault(); fermerMenu(); }
        else if (ev.key === 'ArrowDown') { ev.preventDefault(); boutons[(i + 1) % boutons.length].focus(); }
        else if (ev.key === 'ArrowUp') { ev.preventDefault(); boutons[i === -1 ? boutons.length - 1 : (i - 1 + boutons.length) % boutons.length].focus(); }
        else if (ev.key === 'Home') { ev.preventDefault(); boutons[0].focus(); }
        else if (ev.key === 'End') { ev.preventDefault(); boutons[boutons.length - 1].focus(); }
        else if (ev.key === 'Tab') { fermerMenu(); }
    };
    document.addEventListener('keydown', o.clavier, true);
}

// opts : { titre, aide, label, type ('date' | 'text' | 'number'), valeur, placeholder, ok, annuler, feuille }.
// Résout la valeur saisie (chaîne) à la validation, null si on annule (bouton, Échap, clic à côté).
export function ouvrirFormulaire(ancre, opts) {
    opts = opts || {};
    return new Promise(function(resolve) {
        var id = 'menu-form-' + Date.now().toString(36);
        var contenu = html`<form class="menu-form" method="dialog">
            ${opts.titre ? html`<div class="menu-titre" id="${id}-titre">${opts.titre}</div>` : ''}
            ${opts.aide ? html`<p class="aide">${opts.aide}</p>` : ''}
            <div class="champ"><label for="${id}-champ">${opts.label || 'Valeur'}</label><input id="${id}-champ" class="saisie" type="${opts.type || 'text'}" value="${opts.valeur || ''}" placeholder="${opts.placeholder || ''}" autocomplete="off"${opts.type === 'date' ? '' : html` enterkeyhint="done"`}></div>
            <div class="menu-form-actions"><button type="button" class="btn btn-fantome btn-sm" data-form="annuler">${opts.annuler || 'Annuler'}</button><button type="submit" class="btn btn-primaire btn-sm">${opts.ok || 'Valider'}</button></div>
        </form>`;
        var fini = false;
        function conclure(v) { if (fini) return; fini = true; resolve(v); }
        var o = poser(ancre, contenu, { feuille: opts.feuille !== false, label: opts.titre, role: 'dialog', classe: 'menu-formulaire', alignement: opts.alignement, auMontrer: function(doigt) { var c = o && o.menu.querySelector('input'); if (c) { c.focus(); if (c.select && opts.type !== 'date' && !doigt) c.select(); } } });
        if (!o) { conclure(null); return; }
        if (opts.titre) o.menu.setAttribute('aria-labelledby', id + '-titre');
        o.menu.setAttribute('aria-modal', 'true');
        o.auFermer = function() { conclure(null); };
        var form = o.menu.querySelector('form'), champ = o.menu.querySelector('input');
        form.addEventListener('submit', function(ev) { ev.preventDefault(); var v = (champ.value || '').trim(); if (!v) { champ.focus(); return; } o.auFermer = null; fermerMenu(); conclure(v); });
        o.menu.querySelector('[data-form="annuler"]').addEventListener('click', function() { fermerMenu(); });
        o.clavier = function(ev) {
            if (ev.key === 'Escape') { ev.preventDefault(); fermerMenu(); return; }
            if (ev.key !== 'Tab') return;
            var f = Array.prototype.filter.call(o.menu.querySelectorAll('input, select, textarea, button'), function(el) { return !el.disabled; });
            if (!f.length) return;
            var i = f.indexOf(document.activeElement);
            if (i === -1 || (!ev.shiftKey && i === f.length - 1)) { ev.preventDefault(); f[0].focus(); }
            else if (ev.shiftKey && i === 0) { ev.preventDefault(); f[f.length - 1].focus(); }
        };
        document.addEventListener('keydown', o.clavier, true);
    });
}
window.ouvrirMenu = ouvrirMenu; window.fermerMenu = fermerMenu; window.ouvrirFormulaire = ouvrirFormulaire;
